import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Globe, Pencil } from 'lucide-react'
import { useSiteConfig, useUpdateSite } from '@/hooks/data/useSite'
import { imagesApi } from '@/api/images'
import EditModal from '@/components/crud/EditModal'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'
import { resolveMediaUrl } from '@/utils/mediaUrl';

type SectionKey = 'general' | 'social' | 'contact' | 'shipping' | 'theme'

// Must match ThemeApplier's GOOGLE_FONT_CSS_NAME keys in both storefronts — picking a font
// name outside this list is silently ignored by ThemeApplier rather than 404ing.
const GOOGLE_FONTS = [
  'Outfit', 'Inter', 'Poppins', 'Plus Jakarta Sans', 'Manrope', 'DM Sans', 'Lato', 'Work Sans',
] as const

function parseJson<T extends object>(json: string | undefined, fallback: T): T {
  if (!json) return fallback
  try {
    return { ...fallback, ...JSON.parse(json) }
  } catch {
    return fallback
  }
}

const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'whatsapp', 'tiktok'] as const
type SocialFields = Record<(typeof SOCIAL_PLATFORMS)[number], string>

interface ContactFields {
  email?: string
  phone?: string
  title?: string
  subtitle?: string
  // Real ContactJson also carries serviceAreas/businessHours — not edited by this form,
  // but merged back in on save so this screen can't silently wipe them (same "omitted
  // fields keep their value" contract the PUT endpoint itself documents).
  [key: string]: unknown
}

function SiteSettingsPage() {
  const site = useSiteConfig()
  const update = useUpdateSite()
  const [editingKey, setEditingKey] = useState<SectionKey | null>(null)

  const social = site.data ? parseJson<SocialFields>(site.data.socialJson, {} as SocialFields) : ({} as SocialFields)
  const contact = site.data ? parseJson<ContactFields>(site.data.contactJson, {}) : {}

  const saveGeneral = async (values: Record<string, string>) => {
    if (!site.data) return
    await update.mutateAsync({
      title: values.title,
      applicationName: values.applicationName,
      headerTitle: values.headerTitle,
      description: values.description,
      phone: values.phone,
      acceptingBookings: site.data.acceptingBookings,
      logoUrl: values.logoUrl,
      heroImageUrl: values.heroImageUrl,
      theme: values.theme,
    })
  }

  const saveSocial = async (values: Record<string, string>) => {
    if (!site.data) return
    const next: SocialFields = { ...social }
    for (const p of SOCIAL_PLATFORMS) next[p] = values[p]?.trim() ?? ''
    await update.mutateAsync({
      title: site.data.title,
      applicationName: site.data.applicationName,
      headerTitle: site.data.headerTitle,
      description: site.data.description,
      phone: site.data.phone,
      acceptingBookings: site.data.acceptingBookings,
      socialJson: JSON.stringify(next),
    })
  }

  const saveContact = async (values: Record<string, string>) => {
    if (!site.data) return
    // Merge onto the existing parsed object so serviceAreas/businessHours (real fields
    // this form doesn't expose) survive the round-trip instead of being dropped.
    const next: ContactFields = { ...contact, email: values.email, phone: values.phone, title: values.title, subtitle: values.subtitle }
    await update.mutateAsync({
      title: site.data.title,
      applicationName: site.data.applicationName,
      headerTitle: site.data.headerTitle,
      description: site.data.description,
      phone: site.data.phone,
      acceptingBookings: site.data.acceptingBookings,
      contactJson: JSON.stringify(next),
    })
  }

  const generalFields = [
    { key: 'applicationName', label: 'Site name', required: true },
    { key: 'title', label: 'Page title', required: true },
    { key: 'headerTitle', label: 'Header title' },
    { key: 'description', label: 'Description', type: 'textarea' as const },
    { key: 'phone', label: 'Phone' },
    {
      key: 'theme',
      label: 'Theme',
      type: 'select' as const,
      options: [
        { value: 'system', label: 'Match system' },
        { value: 'light', label: 'Light' },
        { value: 'dark', label: 'Dark' },
      ],
      hint: 'Stored on Site.Theme — not yet wired to any CSS/rendering on either app; this just persists the preference.',
    },
    {
      key: 'logoUrl',
      label: 'Logo',
      type: 'file' as const,
      previewKind: 'image' as const,
      accept: 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml',
      uploadFn: async (file: File) => (await imagesApi.upload(file, 'site')).url,
      hint: 'Uploads via the generic image library (category "site") — same endpoint the Image Library page uses.',
    },
    {
      key: 'heroImageUrl',
      label: 'Hero background image',
      type: 'file' as const,
      previewKind: 'image' as const,
      accept: 'image/jpeg,image/png,image/gif,image/webp',
      uploadFn: async (file: File) => (await imagesApi.upload(file, 'site')).url,
    },
  ]

  const socialFields = SOCIAL_PLATFORMS.map((p) => ({ key: p, label: `${p === 'tiktok' ? 'TikTok' : `${p[0].toUpperCase()}${p.slice(1)}`} URL` }))

  const saveShipping = async (values: Record<string, any>) => {
    if (!site.data) return
    const noFreeShipping = values.freeShippingThreshold === '' || values.freeShippingThreshold == null
    if (values.weightShippingTiersJson) {
      try {
        JSON.parse(values.weightShippingTiersJson)
      } catch {
        throw new Error('Weight tiers must be valid JSON')
      }
    }
    await update.mutateAsync({
      title: site.data.title,
      applicationName: site.data.applicationName,
      headerTitle: site.data.headerTitle,
      description: site.data.description,
      phone: site.data.phone,
      acceptingBookings: site.data.acceptingBookings,
      shippingFlatRate: Number(values.shippingFlatRate) || 0,
      freeShippingThreshold: noFreeShipping ? undefined : Number(values.freeShippingThreshold),
      clearFreeShippingThreshold: noFreeShipping,
      weightShippingTiersJson: values.weightShippingTiersJson || undefined,
    })
  }

  const shippingFields = [
    { key: 'shippingFlatRate', label: 'Flat Shipping Rate ($)', type: 'number' as const, required: true },
    {
      key: 'freeShippingThreshold', label: 'Free Shipping Threshold ($)', type: 'number' as const,
      hint: 'Cart subtotal at or above this ships free. Leave blank to disable free shipping entirely.',
    },
    {
      key: 'weightShippingTiersJson', label: 'Weight-based shipping tiers (JSON)', type: 'textarea' as const,
      hint: 'Array of {"maxWeightKg": number|null, "flatCost": number} or {"maxWeightKg": number|null, "perKgRate": number}, ascending by maxWeightKg with one null-maxWeightKg catch-all tier last. Applied on top of the flat rate above.',
    },
  ]

  const saveTheme = async (values: Record<string, string>) => {
    if (!site.data) return
    await update.mutateAsync({
      title: site.data.title,
      applicationName: site.data.applicationName,
      headerTitle: site.data.headerTitle,
      description: site.data.description,
      phone: site.data.phone,
      acceptingBookings: site.data.acceptingBookings,
      // Sent as-is, including "" — these are plain optional strings server-side (`if not null,
      // overwrite`), so an empty string is how this form clears back to the default, same as
      // the "Reset to default" button below.
      primaryColor: values.primaryColor?.trim() ?? '',
      secondaryColor: values.secondaryColor?.trim() ?? '',
      accentColor: values.accentColor?.trim() ?? '',
      // Only when the form actually carries it: "Reset to default" calls saveTheme without a logo and
      // must not wipe the uploaded one.
      ...(values.logoUrl !== undefined ? { logoUrl: values.logoUrl.trim() } : {}),
      fontFamily: values.fontFamily?.trim() ?? '',
      faviconUrl: values.faviconUrl?.trim() ?? '',
    })
  }

  const themeFields = [
    {
      key: 'logoUrl', label: 'Logo', type: 'file' as const, previewKind: 'image' as const,
      accept: 'image/png,image/svg+xml,image/webp,image/jpeg',
      uploadFn: async (file: File) => (await imagesApi.upload(file, 'site')).url,
      hint: 'Shown in the storefront header instead of the store name. A wide PNG or SVG with a transparent background and light lettering reads best on the header colour; about 40px tall.',
    },
    { key: 'primaryColor', label: 'Primary color', type: 'color' as const, hint: 'Drives the storefront and admin brand color scale (both apps).' },
    { key: 'secondaryColor', label: 'Secondary color', type: 'color' as const, hint: 'Ecommerce storefront only — header and structural chrome (navy in the Kinmel design).' },
    { key: 'accentColor', label: 'Accent color', type: 'color' as const, hint: 'Ecommerce storefront only — footer band and SALE badges (red in the Kinmel design).' },
    {
      key: 'fontFamily', label: 'Font', type: 'select' as const,
      options: GOOGLE_FONTS.map((f) => ({ value: f, label: f })),
    },
    {
      key: 'faviconUrl', label: 'Favicon', type: 'file' as const, previewKind: 'image' as const,
      accept: 'image/png,image/x-icon,image/svg+xml',
      uploadFn: async (file: File) => (await imagesApi.upload(file, 'site')).url,
    },
  ]

  const contactFields = [
    { key: 'email', label: 'Contact email' },
    { key: 'phone', label: 'Contact phone' },
    { key: 'title', label: 'Contact section title' },
    { key: 'subtitle', label: 'Contact section subtitle' },
  ]

  if (site.isLoading) return <p className="p-6 text-gray-500">Loading site settings…</p>
  if (site.isError) return <p className="p-6 text-red-600">Failed to load site settings. Requires the manager role.</p>
  if (!site.data) return <p className="p-6 text-gray-500">No site config yet — save General to create one.</p>

  return (
    <div className="p-2 md:p-4 space-y-4">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
        <Globe className="w-6 h-6" /> Site Settings
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Reads/writes the real live `SiteController` config — what `/api/v1/site/public` actually
        serves to the customer-facing site. The homepage hero (title/subtitle/CTA text) is
        separate, CMS-section-driven content edited under Website (CMS).
      </p>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">General</h2>
          <button onClick={() => setEditingKey('general')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        <dl className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <div><span className="text-gray-400">Site name:</span> {site.data.applicationName || '—'}</div>
          <div><span className="text-gray-400">Page title:</span> {site.data.title || '—'}</div>
          <div><span className="text-gray-400">Phone:</span> {site.data.phone || '—'}</div>
          <div><span className="text-gray-400">Theme:</span> {site.data.theme || 'system'}</div>
        </dl>
        {site.data.logoUrl && (
          <img src={resolveMediaUrl(site.data.logoUrl)} alt="Logo" className="mt-3 h-10 w-auto rounded bg-gray-50 object-contain dark:bg-gray-900" />
        )}
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">Social Links</h2>
          <button onClick={() => setEditingKey('social')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        {SOCIAL_PLATFORMS.every((p) => !social[p]) ? (
          <p className="text-sm text-gray-500">No social links set.</p>
        ) : (
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            {SOCIAL_PLATFORMS.filter((p) => social[p]).map((p) => (
              <li key={p} className="capitalize">{p}: {social[p]}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">Contact</h2>
          <button onClick={() => setEditingKey('contact')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        <dl className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <div><span className="text-gray-400">Phone:</span> {contact.phone || '—'}</div>
          <div><span className="text-gray-400">Email:</span> {contact.email || '—'}</div>
          <div><span className="text-gray-400">Title:</span> {contact.title || '—'}</div>
        </dl>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">Shipping</h2>
          <button onClick={() => setEditingKey('shipping')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <Pencil className="w-4 h-4" />
          </button>
        </div>
        <dl className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <div><span className="text-gray-400">Flat rate:</span> ${site.data.shippingFlatRate.toFixed(2)}</div>
          <div>
            <span className="text-gray-400">Free shipping over:</span>{' '}
            {site.data.freeShippingThreshold != null ? `$${site.data.freeShippingThreshold.toFixed(2)}` : 'Disabled'}
          </div>
        </dl>
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100">Theme & Branding</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={async () => {
                try {
                  await saveTheme({ primaryColor: '', secondaryColor: '', accentColor: '', fontFamily: '', faviconUrl: '' })
                } catch (err) {
                  toast.error(userMessage(err))
                }
              }}
              disabled={update.isPending}
              className="px-2 py-1 text-xs rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            >
              Reset to default
            </button>
            <button onClick={() => setEditingKey('theme')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Applied live to both this dashboard and the storefront by ThemeApplier — no rebuild needed.
        </p>
        <div className="mb-3 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <span className="text-gray-400">Logo:</span>
          {site.data.logoUrl ? (
            <span className="inline-flex items-center rounded bg-secondary-600 px-3 py-2" style={{ backgroundColor: site.data.secondaryColor || '#27367a' }}>
              <img src={resolveMediaUrl(site.data.logoUrl)} alt="Logo" className="h-8 w-auto max-w-[12rem] object-contain" />
            </span>
          ) : (
            <button onClick={() => setEditingKey('theme')} className="rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
              + Upload a logo for the storefront header
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="text-gray-400">Primary:</span>
            {site.data.primaryColor ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-4 w-4 rounded-full border border-gray-300 dark:border-gray-600" style={{ backgroundColor: site.data.primaryColor }} />
                {site.data.primaryColor}
              </span>
            ) : 'Default'}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="text-gray-400">Secondary:</span>
            {site.data.secondaryColor ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-4 w-4 rounded-full border border-gray-300 dark:border-gray-600" style={{ backgroundColor: site.data.secondaryColor }} />
                {site.data.secondaryColor}
              </span>
            ) : 'Default'}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="text-gray-400">Accent:</span>
            {site.data.accentColor ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-4 w-4 rounded-full border border-gray-300 dark:border-gray-600" style={{ backgroundColor: site.data.accentColor }} />
                {site.data.accentColor}
              </span>
            ) : 'Default'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400"><span className="text-gray-400">Font:</span> {site.data.fontFamily || 'Default'}</div>
        </div>
        {site.data.faviconUrl && (
          <img src={resolveMediaUrl(site.data.faviconUrl)} alt="Favicon" className="mt-3 h-8 w-8 rounded bg-gray-50 object-contain dark:bg-gray-900" />
        )}
      </section>

      <EditModal
        isOpen={editingKey === 'general'}
        onClose={() => setEditingKey(null)}
        title="Edit General Settings"
        fields={generalFields}
        initialValues={{
          applicationName: site.data.applicationName,
          title: site.data.title,
          headerTitle: site.data.headerTitle,
          description: site.data.description,
          phone: site.data.phone,
          logoUrl: site.data.logoUrl,
          heroImageUrl: site.data.heroImageUrl,
          theme: site.data.theme || 'system',
        }}
        onSubmit={saveGeneral}
      />

      <EditModal
        isOpen={editingKey === 'social'}
        onClose={() => setEditingKey(null)}
        title="Edit Social Links"
        fields={socialFields}
        initialValues={social}
        onSubmit={saveSocial}
      />

      <EditModal
        isOpen={editingKey === 'contact'}
        onClose={() => setEditingKey(null)}
        title="Edit Contact Info"
        fields={contactFields}
        initialValues={contact}
        onSubmit={saveContact}
      />

      <EditModal
        isOpen={editingKey === 'shipping'}
        onClose={() => setEditingKey(null)}
        title="Edit Shipping Settings"
        fields={shippingFields}
        initialValues={{
          shippingFlatRate: site.data.shippingFlatRate,
          freeShippingThreshold: site.data.freeShippingThreshold ?? '',
          weightShippingTiersJson: (() => {
            try {
              return JSON.stringify(JSON.parse(site.data.weightShippingTiersJson || '[]'), null, 2)
            } catch {
              return site.data.weightShippingTiersJson ?? ''
            }
          })(),
        }}
        onSubmit={saveShipping}
      />

      <EditModal
        isOpen={editingKey === 'theme'}
        onClose={() => setEditingKey(null)}
        title="Edit Theme & Branding"
        fields={themeFields}
        initialValues={{
          primaryColor: site.data.primaryColor || '',
          secondaryColor: site.data.secondaryColor || '',
          accentColor: site.data.accentColor || '',
          logoUrl: site.data.logoUrl || '',
          fontFamily: site.data.fontFamily || GOOGLE_FONTS[0],
          faviconUrl: site.data.faviconUrl || '',
        }}
        onSubmit={saveTheme}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/site/')({
  component: SiteSettingsPage,
})
